import { CloseCircleOutlined, StopOutlined } from '@ant-design/icons';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  EWarehouseCategorySuggestionKind,
  type IAdminCategory,
  type IWarehouseCategorySuggestion,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Button,
  Input,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnsType, type TablePaginationConfig } from 'antd/es/table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENotificationSeverity } from '../../../admin-types';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { getColumnSearch } from '../../fragments/table-get-column-search';
import {
  EWarehouseCategorySuggestionNameConflict,
  getWarehouseCategorySuggestionNameChecks,
  type IWarehouseCategorySuggestionNameCheck,
  WAREHOUSE_CATEGORY_SUGGESTION_STAGE_LABELS,
} from '../../fragments/warehouse-category-suggestion-names';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type NotifyType, useNotify } from '../../notification';
import {
  categoriesWarehouseSuggestionsCancel,
  categoriesWarehouseSuggestionsStart,
  categoryWarehouseSuggestionApply,
  categoryWarehouseSuggestionDismiss,
} from '../../reducers/categories/reducer';
import { EWarehouseCategorySuggestionsRunStatus } from '../../reducers/categories/types';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import WarehouseCategorySuggestionsProgress from './warehouse-category-suggestions-progress';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const KIND_TAGS: Record<
  EWarehouseCategorySuggestionKind,
  { color: string; label: string }
> = {
  [EWarehouseCategorySuggestionKind.PAIR_EXISTING]: {
    color: 'green',
    label: 'Pair',
  },
  [EWarehouseCategorySuggestionKind.CREATE_CHILD]: {
    color: 'blue',
    label: 'New child',
  },
  [EWarehouseCategorySuggestionKind.CREATE_PARENT]: {
    color: 'purple',
    label: 'New parent',
  },
};

const NAME_CONFLICT_LABELS: Record<
  EWarehouseCategorySuggestionNameConflict,
  string
> = {
  [EWarehouseCategorySuggestionNameConflict.EXISTING_CATEGORY]:
    'Name already exists under another parent',
  [EWarehouseCategorySuggestionNameConflict.OTHER_PROPOSAL]:
    'Same name proposed under another parent',
};

const STAGE_OPTIONS = Object.values(EWarehouseCategorySuggestionKind).map(
  (stage: EWarehouseCategorySuggestionKind) => ({
    value: stage,
    label: WAREHOUSE_CATEGORY_SUGGESTION_STAGE_LABELS[stage],
  }),
);

const RUNNING_STATUSES: EWarehouseCategorySuggestionsRunStatus[] = [
  EWarehouseCategorySuggestionsRunStatus.PREPARING,
  EWarehouseCategorySuggestionsRunStatus.RUNNING,
];

const getTargetCategoryUuid = (
  suggestion: IWarehouseCategorySuggestion,
): string | undefined =>
  suggestion.kind === EWarehouseCategorySuggestionKind.PAIR_EXISTING
    ? suggestion.categoryUuid
    : suggestion.parentCategoryUuid;

const isCreateSuggestion = (
  suggestion: IWarehouseCategorySuggestion,
): boolean =>
  suggestion.kind !== EWarehouseCategorySuggestionKind.PAIR_EXISTING;

function WarehouseCategorySuggestions(): React.ReactElement {
  const dispatch = useAppDispatch();
  const notify: NotifyType = useNotify();
  const {
    stage: runStage,
    status,
    items,
    suggestions,
    failedBatchesCount,
    applyingUuids,
  } = useSelector((s: RootState) => s.Categories.warehouseCategorySuggestions);
  const categories = useSelector((s: RootState) => s.Categories.categories);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [editedNames, setEditedNames] = useState<Record<string, string>>({});
  const [skipCache, setSkipCache] = useState<boolean>(false);
  const [stage, setStage] =
    useState<EWarehouseCategorySuggestionKind>(runStage);
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
  }>({ current: 1, pageSize: DEFAULT_PAGE_SIZE });
  const previousStatus = useRef<EWarehouseCategorySuggestionsRunStatus>(status);

  const isRunning: boolean = RUNNING_STATUSES.includes(status);

  useEffect((): void => {
    const wasRunning: boolean = RUNNING_STATUSES.includes(
      previousStatus.current,
    );
    previousStatus.current = status;

    if (!wasRunning) {
      return;
    }

    if (status === EWarehouseCategorySuggestionsRunStatus.DONE) {
      const description = `${suggestions?.length ?? 0} suggestions for ${items.length} warehouse categories`;

      if (failedBatchesCount > 0) {
        notify(
          ENotificationSeverity.WARNING,
          'AI category suggestions finished with errors',
          `${description}, ${failedBatchesCount} batches failed`,
        );
      } else {
        notify(
          ENotificationSeverity.SUCCESS,
          'AI category suggestions finished',
          description,
        );
      }
    }

    if (status === EWarehouseCategorySuggestionsRunStatus.FAILED) {
      notify(
        ENotificationSeverity.ERROR,
        'AI category suggestions failed',
        'Unpaired warehouse categories could not be loaded',
      );
    }
  }, [status, suggestions?.length, items.length, failedBatchesCount, notify]);

  const categoryNameByUuid = useMemo<Map<string, string>>(
    () =>
      new Map(
        (categories ?? []).map((category: IAdminCategory): [string, string] => [
          category.uuid,
          getTranslation(category.names, languageType) || EMPTY_VALUE,
        ]),
      ),
    [categories, languageType],
  );

  const getName = (suggestion: IWarehouseCategorySuggestion): string =>
    editedNames[suggestion.warehouseCategory.uuid] ?? suggestion.name ?? '';

  const nameChecks = useMemo<
    Map<string, IWarehouseCategorySuggestionNameCheck>
  >(
    () =>
      getWarehouseCategorySuggestionNameChecks({
        suggestions: suggestions ?? [],
        getName,
        categories: categories ?? [],
        languageTypeCode: languageType?.code,
      }),
    [suggestions, editedNames, categories, languageType?.code],
  );

  const getNameCheck = (
    suggestion: IWarehouseCategorySuggestion,
  ): IWarehouseCategorySuggestionNameCheck =>
    nameChecks.get(suggestion.warehouseCategory.uuid) ?? {
      groupUuids: [suggestion.warehouseCategory.uuid],
    };

  const columns: ColumnsType<IWarehouseCategorySuggestion> = [
    {
      title: 'Warehouse category',
      key: 'warehouseCategory',
      ...getColumnSearch<IWarehouseCategorySuggestion>(
        'warehouse category',
        (suggestion: IWarehouseCategorySuggestion): string =>
          suggestion.warehouseCategory.name,
      ),
      sorter: (
        a: IWarehouseCategorySuggestion,
        b: IWarehouseCategorySuggestion,
      ): number =>
        stringCompare(a.warehouseCategory.name, b.warehouseCategory.name),
      render: (
        _: unknown,
        suggestion: IWarehouseCategorySuggestion,
      ): React.ReactElement => (
        <Link
          to={`/downloader/warehouse-category/${suggestion.warehouseCategory.uuid}`}
          label={suggestion.warehouseCategory.name}
          openInNewTab
        />
      ),
      width: '22%',
    },
    {
      title: 'Unpaired in stock',
      key: 'inStockProductsCount',
      align: 'right',
      sorter: (
        a: IWarehouseCategorySuggestion,
        b: IWarehouseCategorySuggestion,
      ): number =>
        numberCompare(
          a.warehouseCategory.inStockProductsCount,
          b.warehouseCategory.inStockProductsCount,
        ),
      render: (_: unknown, suggestion: IWarehouseCategorySuggestion): number =>
        suggestion.warehouseCategory.inStockProductsCount,
      width: '7%',
    },
    {
      title: 'Suggestion',
      dataIndex: 'kind',
      filters: Object.values(EWarehouseCategorySuggestionKind).map(
        (kind: EWarehouseCategorySuggestionKind) => ({
          text: KIND_TAGS[kind].label,
          value: kind,
        }),
      ),
      onFilter: (
        value: boolean | React.Key,
        suggestion: IWarehouseCategorySuggestion,
      ): boolean => suggestion.kind === value,
      render: (kind: EWarehouseCategorySuggestionKind): React.ReactElement => (
        <Tag color={KIND_TAGS[kind].color}>{KIND_TAGS[kind].label}</Tag>
      ),
      width: '7%',
    },
    {
      title: 'Target / parent category',
      key: 'target',
      ...getColumnSearch<IWarehouseCategorySuggestion>(
        'category',
        (suggestion: IWarehouseCategorySuggestion): string | undefined => {
          const targetUuid: string | undefined =
            getTargetCategoryUuid(suggestion);

          return targetUuid ? categoryNameByUuid.get(targetUuid) : undefined;
        },
      ),
      render: (
        _: unknown,
        suggestion: IWarehouseCategorySuggestion,
      ): React.ReactElement | string => {
        const targetUuid: string | undefined =
          getTargetCategoryUuid(suggestion);

        if (!targetUuid) {
          return EMPTY_VALUE;
        }

        return (
          <Link
            to={`/category/${targetUuid}`}
            label={categoryNameByUuid.get(targetUuid) ?? EMPTY_VALUE}
            openInNewTab
          />
        );
      },
      width: '15%',
    },
    {
      title: 'New category name',
      key: 'name',
      ...getColumnSearch<IWarehouseCategorySuggestion>('name', getName),
      render: (
        _: unknown,
        suggestion: IWarehouseCategorySuggestion,
      ): React.ReactElement | string => {
        if (!isCreateSuggestion(suggestion)) {
          return EMPTY_VALUE;
        }

        const { groupUuids, conflict, existingCategoryUuid } =
          getNameCheck(suggestion);

        return (
          <Space
            orientation="vertical"
            size={SPACING.xs}
            style={{ width: '100%' }}
          >
            <Input
              size="small"
              value={getName(suggestion)}
              status={conflict ? 'error' : undefined}
              disabled={applyingUuids.includes(
                suggestion.warehouseCategory.uuid,
              )}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                setEditedNames({
                  ...editedNames,
                  [suggestion.warehouseCategory.uuid]: event.target.value,
                })
              }
            />
            <Space size={SPACING.xs} wrap>
              {conflict ? (
                <Tag color="red">{NAME_CONFLICT_LABELS[conflict]}</Tag>
              ) : null}
              {existingCategoryUuid ? (
                <Tooltip title="A category with this name already exists under the same parent — Apply pairs with it">
                  <Tag color="cyan">Exists</Tag>
                </Tooltip>
              ) : null}
              {groupUuids.length > 1 ? (
                <Tooltip title="Other warehouse categories propose the same new category — Apply pairs all of them">
                  <Tag color="gold">Shared by {groupUuids.length}</Tag>
                </Tooltip>
              ) : null}
            </Space>
          </Space>
        );
      },
      width: '20%',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      ...getColumnSearch<IWarehouseCategorySuggestion>('reason'),
      render: (reason: string): React.ReactElement => (
        <Text ellipsis={{ tooltip: reason }}>{reason}</Text>
      ),
      width: '13%',
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      align: 'right',
      sorter: (
        a: IWarehouseCategorySuggestion,
        b: IWarehouseCategorySuggestion,
      ): number => numberCompare(a.confidence, b.confidence),
      render: (confidence: number): string =>
        `${Math.round(confidence * 100)} %`,
      width: '6%',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (
        _: unknown,
        suggestion: IWarehouseCategorySuggestion,
      ): React.ReactElement => {
        const isApplying: boolean = applyingUuids.includes(
          suggestion.warehouseCategory.uuid,
        );
        const name: string = getName(suggestion).trim();
        const { groupUuids, conflict } = getNameCheck(suggestion);

        return (
          <Space size={SPACING.sm}>
            <Button
              size="small"
              type="primary"
              loading={isApplying}
              disabled={
                !languageType ||
                !!conflict ||
                (isCreateSuggestion(suggestion) && !name)
              }
              onClick={(): void => {
                if (!languageType) {
                  return;
                }

                dispatch(
                  categoryWarehouseSuggestionApply(
                    isCreateSuggestion(suggestion)
                      ? { ...suggestion, name }
                      : suggestion,
                    groupUuids,
                    languageType.code,
                  ),
                );
              }}
            >
              {groupUuids.length > 1 ? `Apply (${groupUuids.length})` : 'Apply'}
            </Button>
            <Tooltip title="Dismiss suggestion">
              <Button
                size="small"
                type="text"
                aria-label="Dismiss suggestion"
                icon={<CloseCircleOutlined />}
                disabled={isApplying}
                onClick={(): void => {
                  dispatch(
                    categoryWarehouseSuggestionDismiss(
                      suggestion.warehouseCategory.uuid,
                    ),
                  );
                }}
              />
            </Tooltip>
          </Space>
        );
      },
      width: '10%',
    },
  ];

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <Space size={SPACING.lg} align="center" wrap>
        <Segmented<EWarehouseCategorySuggestionKind>
          options={STAGE_OPTIONS}
          value={stage}
          disabled={isRunning}
          onChange={setStage}
        />

        <Tooltip title="Suggest eshop categories for unpaired warehouse categories with at least one unpaired, visible warehouse product in stock">
          <Button
            type="primary"
            loading={isRunning}
            disabled={isRunning || !languageType}
            onClick={(): void => {
              if (!languageType) {
                return;
              }

              setEditedNames({});
              setPagination({ ...pagination, current: 1 });
              dispatch(
                categoriesWarehouseSuggestionsStart({
                  languageTypeCode: languageType.code,
                  stage,
                  skipCache,
                }),
              );
            }}
          >
            Suggest categories (AI)
          </Button>
        </Tooltip>

        <Tooltip title="Ask AI again even for warehouse categories with a cached suggestion">
          <Switch
            checked={skipCache}
            disabled={isRunning}
            checkedChildren="Ignore cache"
            unCheckedChildren="Use cache"
            onChange={setSkipCache}
          />
        </Tooltip>

        {isRunning ? (
          <Button
            danger
            icon={<StopOutlined />}
            onClick={(): void => {
              dispatch(categoriesWarehouseSuggestionsCancel());
            }}
          >
            Cancel
          </Button>
        ) : null}
      </Space>

      <WarehouseCategorySuggestionsProgress />

      {suggestions ? (
        <Table<IWarehouseCategorySuggestion>
          columns={columns}
          dataSource={suggestions}
          rowKey={(suggestion: IWarehouseCategorySuggestion): string =>
            suggestion.warehouseCategory.uuid
          }
          size="small"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
          onChange={({ current, pageSize }: TablePaginationConfig): void => {
            setPagination({
              current: current ?? 1,
              pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
            });
          }}
          locale={{
            emptyText: isRunning
              ? 'Waiting for the first batch of suggestions…'
              : 'No warehouse categories to suggest',
          }}
        />
      ) : null}
    </Space>
  );
}

export default WarehouseCategorySuggestions;
